import type { JsonValue } from "@e308/core";
import type {
  AcceptedIntent,
  AdmissionReceipt,
  AuthorityState,
  ControllerEnvelope,
  LockedSeat,
} from "./types.js";

const DISCRETE_LIMIT = 16;
const RECEIPT_RETENTION = 256;

export function initialAuthority(seats: readonly LockedSeat[]): AuthorityState & JsonValue {
  return {
    authorityEpoch: 1,
    controllerGenerations: Object.fromEntries(seats.map((seat) => [seat.id, 0])),
    lastControllerSequences: Object.fromEntries(seats.map((seat) => [seat.id, 0])),
    accepted: [],
    receipts: [],
  };
}

export function validateAuthority(value: unknown): AuthorityState & JsonValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Invalid authority state");
  }
  const state = value as AuthorityState;
  if (
    !Number.isSafeInteger(state.authorityEpoch) ||
    state.authorityEpoch < 1 ||
    !plainNumbers(state.controllerGenerations) ||
    !plainNumbers(state.lastControllerSequences) ||
    !Array.isArray(state.accepted) ||
    !Array.isArray(state.receipts)
  ) {
    throw new TypeError("Invalid authority state");
  }
  return state as AuthorityState & JsonValue;
}

export function admitEnvelope(
  state: AuthorityState,
  envelope: ControllerEnvelope,
  assignedBoundaryMs: number,
): {
  readonly state: AuthorityState & JsonValue;
  readonly receipt: AdmissionReceipt;
  readonly duplicate: boolean;
} {
  const priorReceipt = state.receipts.find((entry) => entry.requestId === envelope.requestId);
  if (
    priorReceipt &&
    (priorReceipt.seatId !== envelope.seatId ||
      priorReceipt.controllerSequence !== envelope.controllerSequence)
  ) {
    throw new TypeError("Request ID was already used");
  }
  if (priorReceipt)
    return { state: state as AuthorityState & JsonValue, receipt: priorReceipt, duplicate: true };
  const duplicateSequence = state.receipts.some(
    (entry) =>
      entry.seatId === envelope.seatId && entry.controllerSequence === envelope.controllerSequence,
  );
  if (duplicateSequence) throw new TypeError("Controller sequence was already used");
  const last = state.lastControllerSequences[envelope.seatId] ?? 0;
  if (envelope.controllerSequence <= last) throw new TypeError("Controller sequence is stale");

  const accepted: AcceptedIntent = Object.freeze({ ...envelope, assignedBoundaryMs });
  const settingKey = coalescingKey(accepted);
  const replaced = settingKey
    ? state.accepted.find((entry) => coalescingKey(entry) === settingKey)
    : undefined;
  if (
    !settingKey &&
    discreteCount(state.accepted, envelope.seatId, assignedBoundaryMs) >= DISCRETE_LIMIT
  ) {
    throw new TypeError(`Discrete order limit is ${DISCRETE_LIMIT} per boundary`);
  }
  const coalesced = replaced !== undefined;
  const candidates = [
    ...state.accepted.filter((entry) => coalescingKey(entry) === settingKey),
    accepted,
  ];
  const latest = candidates.reduce((left, right) =>
    left.controllerSequence > right.controllerSequence ? left : right,
  );
  const nextAccepted = settingKey
    ? [...state.accepted.filter((entry) => coalescingKey(entry) !== settingKey), latest]
    : [...state.accepted, accepted];
  const receipt = Object.freeze({
    requestId: envelope.requestId,
    seatId: envelope.seatId,
    controllerSequence: envelope.controllerSequence,
    assignedBoundaryMs,
    coalesced,
  });
  return {
    duplicate: false,
    receipt,
    state: {
      ...state,
      accepted: nextAccepted,
      receipts: [...state.receipts, receipt].slice(-RECEIPT_RETENTION),
    } as unknown as AuthorityState & JsonValue,
  };
}

export function drainBoundary(
  state: AuthorityState,
  boundaryMs: number,
  seatOrder: ReadonlyMap<string, number>,
): { readonly state: AuthorityState & JsonValue; readonly intents: readonly AcceptedIntent[] } {
  const intents = state.accepted
    .filter((entry) => entry.assignedBoundaryMs <= boundaryMs)
    .sort(
      (left, right) =>
        (seatOrder.get(left.seatId) ?? -1) - (seatOrder.get(right.seatId) ?? -1) ||
        left.controllerSequence - right.controllerSequence,
    );
  const sequences = { ...state.lastControllerSequences };
  for (const receipt of state.receipts) {
    if (receipt.assignedBoundaryMs <= boundaryMs) {
      sequences[receipt.seatId] = Math.max(
        sequences[receipt.seatId] ?? 0,
        receipt.controllerSequence,
      );
    }
  }
  return {
    intents: Object.freeze(intents),
    state: {
      ...state,
      lastControllerSequences: sequences,
      accepted: state.accepted.filter((entry) => entry.assignedBoundaryMs > boundaryMs),
    } as unknown as AuthorityState & JsonValue,
  };
}

export function nextControllerSequence(state: AuthorityState, seatId: string): number {
  return (
    Math.max(
      state.lastControllerSequences[seatId] ?? 0,
      ...state.receipts
        .filter((entry) => entry.seatId === seatId)
        .map((entry) => entry.controllerSequence),
    ) + 1
  );
}

function coalescingKey(envelope: AcceptedIntent): string | undefined {
  if (envelope.intent.kind === "set-queue-paused") {
    return `${envelope.assignedBoundaryMs}:${envelope.seatId}:${envelope.intent.kind}:${envelope.intent.queue}`;
  }
  if (envelope.intent.kind === "set-queue-repeat") {
    return `${envelope.assignedBoundaryMs}:${envelope.seatId}:${envelope.intent.kind}:${envelope.intent.queue}:${envelope.intent.orderId}`;
  }
  if (
    envelope.intent.kind === "set-standing-order" ||
    envelope.intent.kind === "set-infrastructure-target"
  ) {
    return `${envelope.assignedBoundaryMs}:${envelope.seatId}:${envelope.intent.kind}:${envelope.intent.borderId}`;
  }
  return envelope.intent.kind === "set-power" ||
    envelope.intent.kind === "set-factory-works" ||
    envelope.intent.kind === "set-power-priority" ||
    envelope.intent.kind === "set-metal-priority" ||
    envelope.intent.kind === "set-construction-split" ||
    envelope.intent.kind === "set-supply" ||
    envelope.intent.kind === "set-shields" ||
    envelope.intent.kind === "set-target"
    ? `${envelope.assignedBoundaryMs}:${envelope.seatId}:${envelope.intent.kind}`
    : undefined;
}

function discreteCount(
  accepted: readonly AcceptedIntent[],
  seatId: string,
  boundaryMs: number,
): number {
  return accepted.filter(
    (entry) =>
      entry.seatId === seatId &&
      entry.assignedBoundaryMs === boundaryMs &&
      coalescingKey(entry) === undefined,
  ).length;
}

function plainNumbers(value: unknown): boolean {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => Number.isSafeInteger(entry) && (entry as number) >= 0)
  );
}
